import React, { useState, useEffect, useRef } from 'react';
import { gtmCalculatorUsed } from '../../../utils/gtm';
import {
  TextField,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Box,
  Slider,
  Tooltip,
  IconButton,
  FormControlLabel,
  Switch,
  Alert,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { NumericFormat } from 'react-number-format';

const MortgagePaymentCalculator = ({
  onCalculationComplete,
  onCalculationError,
  onCalculationStart,
  initialValues,
}) => {
  const calculateMinimumDownPayment = (price) => {
    if (price <= 500000) return price * 0.05;
    if (price <= 1499999) return 500000 * 0.05 + (price - 500000) * 0.1;
    return price * 0.2;
  };

  const getMortgageInsuranceRate = (downPaymentPercent) => {
    if (downPaymentPercent >= 20) return 0;
    if (downPaymentPercent >= 15) return 2.8;
    if (downPaymentPercent >= 10) return 3.1;
    if (downPaymentPercent >= 5) return 4.0;
    return 4.0;
  };

  const formatCurrency = (value) =>
    value.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 });

  const [homePrice, setHomePrice] = useState(initialValues?.homePrice || 500000);
  const [homePriceInput, setHomePriceInput] = useState(
    initialValues?.homePrice ? initialValues.homePrice.toString() : '500000'
  );
  const [downPayment, setDownPayment] = useState(initialValues?.downPayment || 25000);
  const [loanTerm, setLoanTerm] = useState(initialValues?.loanTerm || 5);
  const [amortizationPeriod, setAmortizationPeriod] = useState(initialValues?.amortizationPeriod || 25);
  const [paymentFrequency, setPaymentFrequency] = useState(initialValues?.paymentFrequency || 'monthly');
  const [interestRate, setInterestRate] = useState(
    initialValues?.interestRate ? initialValues.interestRate.toFixed(1) : '5.4'
  );
  const [province, setProvince] = useState('ON');
  const [error, setError] = useState('');
  const [minDownPayment, setMinDownPayment] = useState(calculateMinimumDownPayment(homePrice));

  const [isVariableRate, setIsVariableRate] = useState(false);
  const [minRate, setMinRate] = useState('4.0');
  const [maxRate, setMaxRate] = useState('6.0');

  const [lumpSumAmount, setLumpSumAmount] = useState('');
  const [lumpSumTiming, setLumpSumTiming] = useState('');
  const [extraPaymentPerPeriod, setExtraPaymentPerPeriod] = useState('');

  const toFloatOrZero = (val) => {
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
  };

  const downPaymentPercent = homePrice > 0 ? (downPayment / homePrice) * 100 : 0;

  useEffect(() => {
    const minDP = calculateMinimumDownPayment(homePrice);
    setMinDownPayment(minDP);
    if (downPayment < minDP) setDownPayment(minDP);
    if (downPayment > homePrice) setDownPayment(homePrice);
  }, [homePrice, downPayment]);

  useEffect(() => {
    calculateAndReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    homePrice, interestRate, loanTerm, amortizationPeriod, paymentFrequency, downPayment,
    lumpSumAmount, lumpSumTiming, extraPaymentPerPeriod, isVariableRate, minRate, maxRate, province
  ]);

  const handleHomePriceChange = (values) => {
    setHomePriceInput(values.value);
  };

  const handleHomePriceBlur = () => {
    const numericValue = parseFloat(homePriceInput.replace(/,/g, ''));
    if (!isNaN(numericValue)) {
      setHomePrice(numericValue);
      setHomePriceInput(formatCurrency(numericValue));
    } else {
      setHomePrice(0);
      setHomePriceInput('');
    }
  };

  const handleHomePriceFocus = () => {
    const numericValue = parseFloat(homePriceInput.replace(/[^0-9.-]+/g, ''));
    if (!isNaN(numericValue)) setHomePriceInput(numericValue.toString());
    else setHomePriceInput('');
  };

  const handleInterestRateBlur = () => {
    if (interestRate !== '') {
      const num = parseFloat(interestRate);
      if (!isNaN(num)) setInterestRate(num.toFixed(1));
    }
  };

  const handleMinRateBlur = () => {
    if (minRate !== '') {
      const num = parseFloat(minRate);
      if (!isNaN(num)) setMinRate(num.toFixed(1));
    }
  };

  const handleMaxRateBlur = () => {
    if (maxRate !== '') {
      const num = parseFloat(maxRate);
      if (!isNaN(num)) setMaxRate(num.toFixed(1));
    }
  };

  const handleDownPaymentSliderChange = (event, newValue) => {
    setDownPayment(newValue);
  };

  const calculateMortgage = (annualInterestRate) => {
    if (!homePrice || homePrice <= 0 || !loanTerm || loanTerm <= 0 || !amortizationPeriod || amortizationPeriod <= 0) {
      return { error: 'Asegúrate de que todos los campos sean válidos y mayores a 0.' };
    }

    const principal = homePrice - downPayment;
    const dpPercent = (downPayment / homePrice) * 100;
    const mortgageInsuranceRate = getMortgageInsuranceRate(dpPercent);
    const mortgageInsurance = principal * (mortgageInsuranceRate / 100);
    const totalLoan = principal + mortgageInsurance;

    let paymentsPerYear;
    switch (paymentFrequency) {
      case 'semi-monthly': paymentsPerYear = 24; break;
      case 'bi-weekly':
      case 'accelerated-bi-weekly': paymentsPerYear = 26; break;
      case 'weekly':
      case 'accelerated-weekly': paymentsPerYear = 52; break;
      default: paymentsPerYear = 12;
    }

    if (annualInterestRate < 0) {
      return { error: 'La tasa de interés debe ser positiva.' };
    }

    const nominalRate = annualInterestRate;
    const effectiveAnnualRate = Math.pow(1 + nominalRate / 2, 2) - 1;
    const ratePerPeriod = Math.pow(1 + effectiveAnnualRate, 1 / paymentsPerYear) - 1;
    const totalPayments = paymentsPerYear * amortizationPeriod;
    const paymentsInTerm = paymentsPerYear * loanTerm;

    let basePaymentPerPeriod =
      (totalLoan * ratePerPeriod) / (1 - Math.pow(1 + ratePerPeriod, -totalPayments));

    const extraPayment = toFloatOrZero(extraPaymentPerPeriod);
    let paymentPerPeriod = basePaymentPerPeriod + extraPayment;

    const lumpSumVal = toFloatOrZero(lumpSumAmount);
    const lumpSumYears = toFloatOrZero(lumpSumTiming);
    const lumpSumPeriod = lumpSumYears * paymentsPerYear;

    let balance = totalLoan;
    let currentPaymentIndex = 0;

    for (let i = 0; i < totalPayments && balance > 0; i++) {
      const interestForPeriod = balance * ratePerPeriod;
      const principalPayment = paymentPerPeriod - interestForPeriod;
      balance -= principalPayment;
      if (balance < 0) balance = 0;

      if (lumpSumVal > 0 && i === lumpSumPeriod) {
        balance -= lumpSumVal;
        if (balance < 0) balance = 0;
      }

      currentPaymentIndex = i;
      if (balance <= 0) break;
    }

    const actualNumberOfPayments = balance > 0 ? totalPayments : currentPaymentIndex + 1;

    const pmtCheck = parseFloat(paymentPerPeriod);
    const loanCheck = parseFloat(totalLoan);
    const totalPayCheck = parseFloat(actualNumberOfPayments);
    if (isNaN(pmtCheck) || isNaN(loanCheck) || isNaN(totalPayCheck)) {
      return { error: 'El cálculo devolvió números inválidos.' };
    }

    return {
      paymentPerPeriod: paymentPerPeriod.toFixed(2),
      principalAmount: principal.toFixed(2),
      totalLoan: totalLoan.toFixed(2),
      numberOfPaymentsAmortization: totalPayments,
      numberOfPaymentsTerm: paymentsInTerm,
      mortgageInsurance: mortgageInsurance.toFixed(2),
      amortizationPeriod,
      ratePerPeriod,
      paymentsPerYear,
      totalPayments: actualNumberOfPayments,
      paymentFrequency,
      annualInterestRate,
      homePrice,
      downPayment,
      effectiveAnnualRate,
    };
  };

  const calculateAndReport = () => {
    setError('');
    onCalculationStart && onCalculationStart();

    if (!homePrice || homePrice <= 0) {
      const errMsg = 'Ingresa un precio de propiedad válido.';
      setError(errMsg);
      onCalculationError && onCalculationError(errMsg);
      return;
    }

    if (!loanTerm || loanTerm <= 0 || !amortizationPeriod || amortizationPeriod <= 0) {
      const errMsg = 'Configura un plazo y período de amortización válidos.';
      setError(errMsg);
      onCalculationError && onCalculationError(errMsg);
      return;
    }

    if (isVariableRate) {
      const minR = parseFloat(minRate);
      const maxR = parseFloat(maxRate);
      if (isNaN(minR) || isNaN(maxR) || minR < 0 || maxR < 0) {
        const errMsg = 'Ingresa tasas mínima y máxima válidas.';
        setError(errMsg);
        onCalculationError && onCalculationError(errMsg);
        return;
      }

      const minResult = calculateMortgage(minR / 100);
      const maxResult = calculateMortgage(maxR / 100);

      if (minResult.error) { setError(minResult.error); onCalculationError && onCalculationError(minResult.error); return; }
      if (maxResult.error) { setError(maxResult.error); onCalculationError && onCalculationError(maxResult.error); return; }

      onCalculationComplete && onCalculationComplete({ minResult, maxResult, isVariable: true });
      gtmCalculatorUsed({ calculator: 'mortgage_payment', action: 'calculate', value: 'variable' });
    } else {
      const ir = parseFloat(interestRate);
      if (isNaN(ir) || ir < 0) {
        const errMsg = 'Ingresa una tasa de interés válida.';
        setError(errMsg);
        onCalculationError && onCalculationError(errMsg);
        return;
      }
      const result = calculateMortgage(ir / 100);
      if (result.error) { setError(result.error); onCalculationError && onCalculationError(result.error); return; }
      onCalculationComplete && onCalculationComplete({ ...result, isVariable: false });
      gtmCalculatorUsed({ calculator: 'mortgage_payment', action: 'calculate', value: 'fixed' });
    }
  };

  const focusSx = {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': { borderColor: '#1E3A5F' },
      '&:hover fieldset': { borderColor: '#1E3A5F' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#1E3A5F' },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: { xs: '20px', md: '28px' },
        borderRadius: '16px',
        border: '1px solid #E6E9EE',
        boxShadow: '0 1px 8px rgba(11,18,32,0.06)',
        backgroundColor: '#fff',
      }}
    >
      <Typography variant="h4" gutterBottom align="center" sx={{ color: '#1A1A1A', mb: 3 }}>
        Calculadora de Hipotecas
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>
      )}

      {/* Precio de la propiedad */}
      <NumericFormat
        label="Precio de la propiedad"
        value={homePriceInput}
        customInput={TextField}
        thousandSeparator=","
        decimalSeparator="."
        prefix="$"
        onValueChange={handleHomePriceChange}
        onBlur={handleHomePriceBlur}
        onFocus={handleHomePriceFocus}
        fullWidth
        margin="normal"
        variant="outlined"
        InputProps={{ inputProps: { inputMode: 'decimal', pattern: '[0-9.]*' } }}
        helperText="Ingresa el precio total de la propiedad."
        sx={focusSx}
      />

      {/* Pago inicial */}
      <Box sx={{ mt: 3 }}>
        <Typography
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, color: '#1A1A1A', fontSize: '0.95rem' }}
        >
          Pago inicial: {formatCurrency(downPayment)} ({downPaymentPercent.toFixed(1)}%)
          <Tooltip title="Ajusta el pago inicial para ver cómo afecta tu hipoteca.">
            <IconButton size="small" sx={{ ml: 1, color: '#1E3A5F' }} aria-label="Más información">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <Slider
          value={downPayment}
          min={0}
          max={homePrice}
          step={1000}
          onChange={handleDownPaymentSliderChange}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => formatCurrency(value)}
          sx={{
            color: '#1E3A5F',
            '& .MuiSlider-thumb': { backgroundColor: '#fff', border: '2px solid #1E3A5F' },
          }}
        />
        <Typography variant="body2" sx={{ color: '#5F6B7A' }}>
          El pago inicial mínimo es {formatCurrency(minDownPayment)}
        </Typography>

        {/* CMHC Warning */}
        {downPaymentPercent < 20 && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon sx={{ color: '#D32F2F' }} />}
            sx={{
              mt: 1.5,
              borderRadius: '0 10px 10px 0',
              bgcolor: '#F7F9FC',
              borderLeft: '4px solid #D32F2F',
              '& .MuiAlert-icon': { alignItems: 'center' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Seguro CMHC requerido
            </Typography>
            <Typography variant="caption">
              Con un pago inicial menor al 20%, necesitas seguro hipotecario (CMHC).
              La prima se agrega al monto de tu hipoteca. Tasa actual: {getMortgageInsuranceRate(downPaymentPercent)}%.
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Provincia */}
      <Box sx={{ mt: 2 }}>
        <Typography gutterBottom sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.95rem' }}>
          Provincia
          <Tooltip title="El impuesto de transferencia de propiedad varía por provincia.">
            <IconButton size="small" sx={{ ml: 1, color: '#1E3A5F' }} aria-label="Más información">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <FormControl fullWidth margin="normal" sx={focusSx}>
          <InputLabel>Provincia</InputLabel>
          <Select value={province} label="Provincia" onChange={(e) => setProvince(e.target.value)}>
            <MenuItem value="ON">Ontario</MenuItem>
            <MenuItem value="QC">Quebec</MenuItem>
            <MenuItem value="BC">Columbia Británica</MenuItem>
            <MenuItem value="AB">Alberta</MenuItem>
            <MenuItem value="OTHER">Otra provincia</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Tasa variable toggle */}
      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={<Switch checked={isVariableRate} onChange={(e) => setIsVariableRate(e.target.checked)} sx={{ '& .Mui-checked': { color: '#1E3A5F' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#1E3A5F' } }} />}
          label="Tasa de interés variable"
          sx={{ '& .MuiFormControlLabel-label': { fontWeight: 500, color: '#1A1A1A' } }}
        />
      </Box>

      {/* Tasa de interes */}
      {!isVariableRate ? (
        <Box sx={{ mt: 2 }}>
          <Typography gutterBottom sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.95rem' }}>
            Tasa de interés (%)
            <Tooltip title="La tasa de interés anual de tu hipoteca.">
              <IconButton size="small" sx={{ ml: 1, color: '#1E3A5F' }} aria-label="Más información">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
          <TextField
            value={interestRate}
            onChange={(e) => setInterestRate(e.target.value)}
            onBlur={handleInterestRateBlur}
            type="text"
            fullWidth
            margin="normal"
            sx={focusSx}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              inputProps: { step: 0.1, inputMode: 'decimal' }
            }}
          />
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Typography gutterBottom sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.95rem' }}>
            Tasa mínima (%)
          </Typography>
          <TextField value={minRate} onChange={(e) => setMinRate(e.target.value)} onBlur={handleMinRateBlur} type="text" fullWidth margin="normal" sx={focusSx}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment>, inputProps: { step: 0.1, inputMode: 'decimal' } }}
          />
          <Typography gutterBottom sx={{ mt: 2, fontWeight: 600, color: '#1A1A1A', fontSize: '0.95rem' }}>
            Tasa máxima (%)
          </Typography>
          <TextField value={maxRate} onChange={(e) => setMaxRate(e.target.value)} onBlur={handleMaxRateBlur} type="text" fullWidth margin="normal" sx={focusSx}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment>, inputProps: { step: 0.1, inputMode: 'decimal' } }}
          />
        </Box>
      )}

      {/* Plazo del prestamo */}
      <Box sx={{ mt: 2 }}>
        <Typography gutterBottom sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.95rem' }}>
          Plazo del préstamo (años)
          <Tooltip title="El período durante el cual los términos de tu hipoteca son fijos.">
            <IconButton size="small" sx={{ ml: 1, color: '#1E3A5F' }} aria-label="Más información">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <FormControl fullWidth margin="normal" sx={focusSx}>
          <InputLabel>Plazo (años)</InputLabel>
          <Select value={loanTerm} label="Plazo (años)" onChange={(e) => setLoanTerm(parseInt(e.target.value, 10) || 0)}>
            {[...Array(10).keys()].map((i) => (
              <MenuItem key={i + 1} value={i + 1}>
                {i + 1} {i + 1 === 1 ? 'año' : 'años'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Periodo de amortizacion */}
      <Box sx={{ mt: 2 }}>
        <Typography gutterBottom sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.95rem' }}>
          Período de amortización (años)
          <Tooltip title="Tiempo total para pagar tu hipoteca completamente.">
            <IconButton size="small" sx={{ ml: 1, color: '#1E3A5F' }} aria-label="Más información">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <FormControl fullWidth margin="normal" sx={focusSx}>
          <InputLabel>Amortización (años)</InputLabel>
          <Select value={amortizationPeriod} label="Amortización (años)" onChange={(e) => setAmortizationPeriod(parseInt(e.target.value, 10) || 0)}>
            {[15, 20, 25, 30].map((period) => (
              <MenuItem key={period} value={period}>{period} años</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Frecuencia de pago */}
      <Box sx={{ mt: 2 }}>
        <Typography gutterBottom sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.95rem' }}>
          Frecuencia de pago
          <Tooltip title="Con qué frecuencia realizas tus pagos hipotecarios.">
            <IconButton size="small" sx={{ ml: 1, color: '#1E3A5F' }} aria-label="Más información">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <FormControl fullWidth margin="normal" sx={focusSx}>
          <InputLabel>Frecuencia de pago</InputLabel>
          <Select value={paymentFrequency} label="Frecuencia de pago" onChange={(e) => setPaymentFrequency(e.target.value)}>
            <MenuItem value="monthly">Mensual</MenuItem>
            <MenuItem value="semi-monthly">Quincenal</MenuItem>
            <MenuItem value="bi-weekly">Bisemanal</MenuItem>
            <MenuItem value="accelerated-bi-weekly">Bisemanal acelerado</MenuItem>
            <MenuItem value="weekly">Semanal</MenuItem>
            <MenuItem value="accelerated-weekly">Semanal acelerado</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Opciones de prepago */}
      <Box
        sx={{
          mt: 4,
          bgcolor: '#FFFFFF',
          border: '1px solid #E6E9EE',
          borderRadius: '16px',
          p: { xs: '20px', md: '24px' },
        }}
      >
        <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1A1A1A', mb: 5 }}>
          Opciones de prepago
        </Typography>

        {/* Lump sum group */}
        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 2 }}>
          Pago único (lump-sum)
        </Typography>
        <TextField
          label="Monto del pago único"
          type="text"
          value={lumpSumAmount}
          onChange={(e) => setLumpSumAmount(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Pago único aplicado después del número de años seleccionado."
          sx={focusSx}
          InputProps={{ inputProps: { inputMode: 'decimal' } }}
        />
        <TextField
          label="Aplicar después de (años)"
          type="text"
          value={lumpSumTiming}
          onChange={(e) => setLumpSumTiming(e.target.value)}
          fullWidth
          margin="normal"
          helperText="El pago único se aplicará después de estos años."
          sx={focusSx}
          InputProps={{ inputProps: { inputMode: 'numeric' } }}
        />

        {/* Extra payment group */}
        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: '0.04em', mt: 6, mb: 2 }}>
          Pago adicional por período
        </Typography>
        <TextField
          label="Monto adicional"
          type="text"
          value={extraPaymentPerPeriod}
          onChange={(e) => setExtraPaymentPerPeriod(e.target.value)}
          fullWidth
          margin="normal"
          helperText="Monto adicional agregado a cada pago hipotecario programado."
          sx={focusSx}
          InputProps={{ inputProps: { inputMode: 'decimal' } }}
        />
      </Box>
    </Paper>
  );
};

export default MortgagePaymentCalculator;
